/** What the Anthropic API reported for the finished message, forwarded once before `[DONE]`. */
export interface FinalReport {
  model: string;
  stop_reason: string | null;
  output_tokens: number;
}

export type StreamEvent =
  | { kind: 'text'; text: string }
  | { kind: 'final'; report: FinalReport }
  | { kind: 'error'; error: string; code: string }
  | { kind: 'done' };

export class StreamProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StreamProtocolError';
  }
}

export const excerpt = (data: string): string => (data.length > 120 ? `${data.slice(0, 120)}…` : data);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const own = Object.keys(value);
  return own.length === keys.length && keys.every((k) => Object.hasOwn(value, k));
}

function parseFinal(value: unknown): FinalReport | null {
  if (!isRecord(value) || !hasExactKeys(value, ['model', 'stop_reason', 'output_tokens'])) return null;
  const { model, stop_reason, output_tokens } = value;
  if (typeof model !== 'string' || model === '') return null;
  if (stop_reason !== null && (typeof stop_reason !== 'string' || stop_reason === '')) return null;
  if (typeof output_tokens !== 'number' || !Number.isInteger(output_tokens) || output_tokens < 0) return null;
  return { model, stop_reason, output_tokens };
}

/** Parses one SSE `data:` payload from /api/generate. Anything that is not an exact known shape throws. */
export function parseStreamEvent(data: string): StreamEvent {
  if (data === '[DONE]') return { kind: 'done' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    throw new StreamProtocolError(`The server sent a malformed stream event: ${excerpt(data)}`);
  }
  if (isRecord(parsed)) {
    if (hasExactKeys(parsed, ['text']) && typeof parsed.text === 'string') {
      return { kind: 'text', text: parsed.text };
    }
    if (hasExactKeys(parsed, ['error', 'code']) && typeof parsed.error === 'string' && typeof parsed.code === 'string') {
      return { kind: 'error', error: parsed.error, code: parsed.code };
    }
    if (hasExactKeys(parsed, ['final'])) {
      const report = parseFinal(parsed.final);
      if (report !== null) return { kind: 'final', report };
    }
  }
  throw new StreamProtocolError(`The server sent an unknown stream event: ${excerpt(data)}`);
}

/**
 * Enforces the event order: text…, exactly one final, then [DONE]; or text…, then one error.
 * Throws on the first event that breaks it.
 */
export class StreamSequence {
  private final = false;
  private closed = false;

  accept(event: StreamEvent): void {
    if (this.closed) throw new StreamProtocolError(`The server sent a ${event.kind} event after the stream had ended.`);
    switch (event.kind) {
      case 'text':
        if (this.final) throw new StreamProtocolError('The server sent text after the final event.');
        return;
      case 'final':
        if (this.final) throw new StreamProtocolError('The server sent a second final event.');
        this.final = true;
        return;
      case 'error':
        this.closed = true;
        return;
      case 'done':
        if (!this.final) throw new StreamProtocolError('The stream ended with [DONE] but no final event.');
        this.closed = true;
        return;
    }
  }

  get ended(): boolean {
    return this.closed;
  }
}
